'use client'

import { useRouter, usePathname } from 'next/navigation'
import { useAuth } from '@/components/auth/auth-provider'
import { useProfile } from '@/hooks/use-profile'
import { useSupabase } from '@/hooks/use-supabase'
import { Button } from '@/components/ui/button'
import { AvatarDisplay } from '@/components/ui/avatar-display'
import {
  Home,
  Plus,
  TrendingUp,
  Settings,
  LogOut,
  Menu,
  X,
  ChevronLeft,
  ChevronRight,
  Scan,
  DollarSign,
  Upload,
  ListTodo,
  BarChart3,
  CheckSquare,
  Tag
} from 'lucide-react'
import { useState, useEffect } from 'react'
import { useSubscription } from '@/hooks/use-subscription'
import { hasFeature } from '@/lib/features'
import { alerts } from '@/lib/alerts'
import { ThemeToggle } from '@/components/ui/theme-toggle'

interface SidebarProps {
  user?: {
    id?: string
    email?: string
    user_metadata?: {
      full_name?: string
    }
  }
  onLogout: () => void
  onScannerClick?: () => void
}


const menuItems = [
  { id: 'dashboard', label: 'Dashboard', icon: Home, path: '/dashboard' },
  { id: 'expenses', label: 'Agregar Gasto', icon: DollarSign, path: '/dashboard/add-expense' },
  { id: 'income', label: 'Agregar Ingreso', icon: TrendingUp, path: '/dashboard/add-income' },
  { id: 'scanner', label: 'Escanear Ticket', icon: Upload, path: '/dashboard' },
  { id: 'checklist', label: 'Pagos Mensuales', icon: CheckSquare, path: '/dashboard/checklist' },
  { id: 'transactions', label: 'Todas las Transacciones', icon: ListTodo, path: '/dashboard/transactions' },
  { id: 'categories', label: 'Categorías', icon: Tag, path: '/dashboard/categories' },
  { id: 'reports', label: 'Reportes', icon: BarChart3, path: '/dashboard/reports' },
  { id: 'support', label: 'Soporte', icon: Settings, path: '/dashboard/support' },
]

// Items principales para la barra inferior móvil (máx 5 para usabilidad)
const mobileNavItems = [
  { id: 'dashboard', label: 'Inicio', icon: Home, path: '/dashboard' },
  { id: 'expenses', label: 'Gasto', icon: DollarSign, path: '/dashboard/add-expense' },
  { id: 'income', label: 'Ingreso', icon: TrendingUp, path: '/dashboard/add-income' },
  { id: 'reports', label: 'Reportes', icon: BarChart3, path: '/dashboard/reports' },
  { id: 'more', label: 'Más', icon: Menu, path: '' },
]

// Items del menú "Más" en móvil
const mobileMoreItems = [
  { id: 'transactions', label: 'Todas las Transacciones', icon: ListTodo, path: '/dashboard/transactions' },
  { id: 'scanner', label: 'Escanear Ticket', icon: Upload, path: '/dashboard' },
  { id: 'checklist', label: 'Pagos Mensuales', icon: CheckSquare, path: '/dashboard/checklist' },
  { id: 'categories', label: 'Categorías', icon: Tag, path: '/dashboard/categories' },
  { id: 'support', label: 'Soporte', icon: Settings, path: '/dashboard/support' },
]

export default function Sidebar({ user, onLogout, onScannerClick }: SidebarProps) {
  const router = useRouter()
  const pathname = usePathname()
  const supabase = useSupabase()
  const { avatarColor } = useProfile()
  const getUserInitials = (): string => {
    if (!user) return 'U'

    // Obtener el nombre del usuario
    const name = (user.user_metadata?.full_name || user.email || '').toString().trim()

    // Si no hay nombre, devolver 'U'
    if (!name) return 'U'

    // Obtener las iniciales (primera letra de cada palabra, máximo 2)
    const initials = name
      .split(' ')
      .map((part: string) => part[0])
      .join('')
      .toUpperCase()
      .substring(0, 2)

    return initials || 'U'
  }

  const userName = user?.user_metadata?.full_name || user?.email?.split('@')[0] || 'U'
  const getFirstName = (): string => userName.split(' ')[0]
  const [collapsed, setCollapsed] = useState(false)
  const [userAvatar, setUserAvatar] = useState('default')
  const [isMobile, setIsMobile] = useState(false)
  const [moreMenuOpen, setMoreMenuOpen] = useState(false)

  // Detectar si es móvil
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth < 768)
    }

    checkIfMobile()
    window.addEventListener('resize', checkIfMobile)
    return () => window.removeEventListener('resize', checkIfMobile)
  }, [])

  const getUserDisplayName = (): string => {
    if (!user) return 'Usuario'

    // Verificar si user_metadata existe y tiene full_name
    if (user.user_metadata?.full_name) {
      return user.user_metadata.full_name
    }

    // Si no hay user_metadata o no tiene full_name, usar el email
    if (user.email) {
      return user.email.split('@')[0]
    }

    // Si no hay nada, devolver 'Usuario' como valor por defecto
    return 'Usuario'
  }

  const { data: sub } = useSubscription()

  const handleMenuClick = async (item: typeof menuItems[0]) => {
    if (item.id === 'scanner') {
      if (!hasFeature('scanner_ai', sub)) {
        await alerts.info('Función Pro', 'El escaneo de tickets con IA está disponible en el plan Pro o durante la prueba gratuita. Cambia de plan para usarlo.')
        router.push('/dashboard/plans')
        return
      }
      onScannerClick?.()
      return
    }
    if (item.id === 'checklist') {
      if (!hasFeature('recurring_checklist', sub)) {
        await alerts.info('Disponible en Plus/Pro', 'La checklist de pagos mensuales está disponible en los planes Plus y Pro (o durante la prueba gratuita).')
        router.push('/dashboard/plans')
        return
      }
    }
    router.push(item.path)
  }


  // Cargar avatar del usuario desde la base de datos
  useEffect(() => {
    const loadUserAvatar = async () => {
      if (!user?.id) return

      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('avatar_url')
          .eq('id', user.id)
          .single()

        if (!error && data?.avatar_url) {
          setUserAvatar(data.avatar_url)
        }
      } catch (error) {
        console.error('Error loading user avatar:', error)
      }
    }

    loadUserAvatar()
  }, [user?.id, supabase])

  // Renderizar condicionalmente basado en JavaScript
  if (isMobile) {
    return (
      <>
        {/* Header móvil */}
        <div className="fixed top-0 left-0 right-0 z-40 bg-header text-header-foreground px-4 py-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-header-foreground/70 leading-tight">Hola,</p>
              <h1 className="font-display text-2xl font-bold leading-tight">
                {getFirstName()}
              </h1>
            </div>
            <div className="flex items-center space-x-2">
              <ThemeToggle />
              <button
                onClick={() => router.push('/dashboard/profile')}
                className="h-10 w-10 flex items-center justify-center rounded-full hover:bg-header-foreground/10 transition-colors"
                title="Editar perfil"
              >
                <AvatarDisplay
                  avatarId={avatarColor}
                  size="sm"
                  name={getUserInitials()}
                  colorClassName="bg-black"
                  className="cursor-pointer"
                />
              </button>
              <Button
                variant="ghost"
                size="icon"
                onClick={onLogout}
                className="h-8 w-8 text-header-foreground hover:bg-header-foreground/10 hover:text-header-foreground"
                title="Cerrar sesión"
              >
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>

        {/* Menú "Más" overlay */}
        {moreMenuOpen && (
          <>
            <div className="fixed inset-0 z-40 bg-black/40" onClick={() => setMoreMenuOpen(false)} />
            <div className="fixed bottom-16 left-0 right-0 z-50 bg-secondary border-t border-border rounded-t-2xl shadow-xl p-4 animate-in slide-in-from-bottom-4 duration-200">
              <div className="grid grid-cols-3 gap-3">
                {mobileMoreItems.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => {
                      setMoreMenuOpen(false)
                      handleMenuClick(item)
                    }}
                    className={`flex flex-col items-center justify-center p-3 rounded-xl transition-colors ${pathname === item.path
                      ? 'text-primary bg-primary/10'
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                    }`}
                  >
                    <item.icon className="h-6 w-6 mb-1.5" />
                    <span className="text-xs text-center leading-tight">{item.label}</span>
                  </button>
                ))}
              </div>
            </div>
          </>
        )}

        {/* Bottom Navigation — 5 items fijos */}
        <div className="fixed bottom-0 left-0 right-0 z-40 bg-secondary border-t border-border">
          <nav className="flex items-center justify-around py-2 px-1">
            {mobileNavItems.map((item) => {
              const isMore = item.id === 'more'
              const isActive = isMore ? moreMenuOpen : pathname === item.path
              return (
                <button
                  key={item.id}
                  onClick={() => {
                    if (isMore) {
                      setMoreMenuOpen(!moreMenuOpen)
                    } else {
                      setMoreMenuOpen(false)
                      handleMenuClick(item)
                    }
                  }}
                  className={`flex flex-col items-center justify-center p-2 rounded-lg transition-colors min-w-[56px] ${isActive
                    ? 'text-primary bg-primary/10'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                  }`}
                >
                  <item.icon className="h-5 w-5 mb-1" />
                  <span className="text-[11px] text-center">{item.label}</span>
                </button>
              )
            })}
          </nav>
        </div>
      </>
    )
  }

  // Sidebar para escritorio
  return (
    <div className={`bg-secondary border-r border-border transition-all duration-200 ease-in-out h-screen flex flex-col fixed left-0 top-0 z-30 ${collapsed ? 'w-14 lg:w-16' : 'w-56 lg:w-64'
      }`}>
      {/* Header */}
      <div className="p-4 py-6 bg-header text-header-foreground">
        <div className="flex items-center justify-between">
          {!collapsed && (
            <div>
              <p className="text-sm text-header-foreground/70 leading-tight">Hola,</p>
              <h1 className="font-display text-2xl font-bold leading-tight">
                {getFirstName()}
              </h1>
            </div>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setCollapsed(!collapsed)}
            className="h-8 w-8 p-0 text-header-foreground hover:bg-header-foreground/10 hover:text-header-foreground"
          >
            {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
            <span className="sr-only">{collapsed ? 'Expandir' : 'Contraer'}</span>
          </Button>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-4 bg-transparent">
        <ul className="space-y-1 px-2">
          {menuItems.map((item) => {
            const isActive = pathname === item.path;
            return (
              <button
                key={item.id}
                onClick={() => handleMenuClick(item)}
                className={`w-full flex items-center px-3 py-2 rounded-lg mb-1 transition-colors relative group ${isActive
                  ? 'bg-primary/10 text-primary'
                  : 'text-muted-foreground hover:bg-accent hover:text-foreground'
                  } ${collapsed ? 'justify-center' : ''}`}
                title={collapsed ? item.label : undefined}
              >
                <item.icon className={`h-5 w-5 ${isActive ? 'text-primary' : ''}`} />
                {!collapsed && (
                  <span className="ml-3 font-medium text-sm truncate flex-1 text-left">
                    {item.label}
                  </span>
                )}

                {/* Indicadores de estado (Candado o Trial) */}
                {!collapsed && (
                  <>
                    {/* Caso: Bloqueado (No tiene feature) */}
                    {((item.id === 'scanner' && !hasFeature('scanner_ai', sub)) ||
                      (item.id === 'checklist' && !hasFeature('recurring_checklist', sub))) && (
                        <div className="ml-2">
                          <div className="h-1.5 w-1.5 rounded-full bg-muted-foreground/40" />
                        </div>
                      )}

                    {/* Caso: Desbloqueado por Trial */}
                    {sub?.trialActive && ((item.id === 'scanner' && hasFeature('scanner_ai', sub)) ||
                      (item.id === 'checklist' && hasFeature('recurring_checklist', sub))) && (
                        <span className="ml-2 text-[10px] font-bold text-teal-500 bg-teal-500/10 px-1.5 py-0.5 rounded-full">
                          TRIAL
                        </span>
                      )}
                  </>
                )}
              </button>
            )
          })}
        </ul>
      </nav>

      {/* User Section */}
      <div className="mt-auto bg-transparent">
        <div className={`p-4 border-t border-border bg-transparent ${collapsed ? 'px-2' : ''}`}>
          <button
            onClick={() => router.push('/dashboard/profile')}
            className="flex items-center space-x-3 w-full hover:bg-muted/50 rounded-lg p-2 transition-colors"
            title="Editar perfil"
          >
            <div className="relative">
              <div className={collapsed ? 'h-8 w-8' : 'h-10 w-10'}>
                <AvatarDisplay
                  avatarId={avatarColor}
                  size={collapsed ? 'sm' : 'md'}
                  name={getUserInitials()}
                />
              </div>
            </div>
            {!collapsed && (
              <div className="flex-1 min-w-0 text-left">
                <p className="text-sm font-medium truncate">{getUserDisplayName()}</p>
                <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
                {sub?.trialActive && (
                  <span className="inline-block mt-1 px-1.5 py-0.5 rounded-full border border-yellow-400/30 text-yellow-300 bg-yellow-500/10 text-[10px]" title="Estás usando la versión Pro en prueba por 7 días">
                    Trial Pro
                  </span>
                )}
              </div>
            )}
          </button>
        </div>

        <div className={`p-4 border-t border-border bg-transparent ${collapsed ? 'px-2' : ''}`}>
          <div className={`flex items-center ${collapsed ? 'justify-center mb-2' : 'justify-between mb-4'}`}>
            <ThemeToggle />
            {!collapsed && (
              <div className="flex items-center gap-2 text-xs">
                <button
                  onClick={() => router.push('/privacy')}
                  className="text-muted-foreground hover:text-foreground hover:underline"
                >
                  Privacidad
                </button>
                <span className="text-muted-foreground">·</span>
                <button
                  onClick={() => router.push('/terms')}
                  className="text-muted-foreground hover:text-foreground hover:underline"
                >
                  Términos
                </button>
              </div>
            )}
          </div>
          <Button
            variant="ghost"
            size={collapsed ? 'icon' : 'default'}
            onClick={onLogout}
            className={`w-full ${collapsed ? 'justify-center' : 'justify-start'}`}
            title={collapsed ? 'Cerrar sesión' : undefined}
          >
            <LogOut className="h-5 w-5" />
            {!collapsed && <span className="ml-3">Cerrar sesión</span>}
          </Button>
        </div>
      </div>
    </div>
  )
}
